import { useMutation } from "@apollo/client";
import { Stack, TextField } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { Dialog } from "components/Dialog";
import EncampAutocomplete from "components/Forms/EncampAutocomplete";
import { FormSelect, SelectItem } from "components/Forms/FormSelect";
import { FormTextField } from "components/Forms/FormTextField";
import { ToggleField } from "components/Forms/ToggleField";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { CADENCE_LABELS, INTAKE_LABELS } from "encamp-shared/src/waste/vendorDataCompleteness";
import {
  UpsertTenantWasteVendorInput,
  WasteVendorDeliveryCadence,
  WasteVendorIntake,
} from "generated-graphql/graphql";
import { useEffect, useState } from "react";
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form";

import {
  UPSERT_TENANT_WASTE_VENDOR,
  VENDOR_DATA_COMPLETENESS_QUERY,
  VENDOR_DATA_TENANTS_QUERY,
  WASTE_MANIFEST_VENDORS_TABLE_QUERY,
} from "./api";
import { VendorDataRow } from "./columns";

export type ExpectationFormType = {
  tenantId: string;
  wasteManifestVendorId: string;
  cadence: string | null;
  dueByDayOfMonth: number | null;
  intake: string | null;
  isActive: boolean;
  notes: string;
};

export interface ExpectationPrefill {
  tenantId: string;
  wasteManifestVendorId: string;
  /**
   * Display names for the identity-locked fields. Optional because the
   * dialog can still resolve ids alone (it just shows the raw id until a
   * name is available) — callers that already have the names on hand (the
   * observed-data scan does) should pass them so the dialog never has to.
   */
  tenantName?: string;
  vendorName?: string;
}

interface ExpectationDialogProps {
  open: boolean;
  onClose: () => void;
  row: VendorDataRow | null;
  prefill?: ExpectationPrefill;
}

type TenantOption = { id: string; name: string };
type VendorOption = { id: string; name: string };

// Record<string, ...> views over the shared label maps — see columns.tsx for
// why a generic-string lookup is used instead of an `as` cast.
const CADENCE_LABEL_LOOKUP: Record<string, string> = CADENCE_LABELS;
const INTAKE_LABEL_LOOKUP: Record<string, string> = INTAKE_LABELS;

// "None" is an explicit option (not just an initially-empty select) so an
// unset schedule reads as a deliberate choice, not a loading state.
const CADENCE_SELECT_ITEMS: SelectItem[] = [
  { value: "", display: "None" },
  ...Object.values(WasteVendorDeliveryCadence).map((value) => ({
    value,
    display: CADENCE_LABEL_LOOKUP[value] ?? value,
  })),
];

const INTAKE_SELECT_ITEMS: SelectItem[] = [
  { value: "", display: "None" },
  ...Object.values(WasteVendorIntake).map((value) => ({
    value,
    display: INTAKE_LABEL_LOOKUP[value] ?? value,
  })),
];

function isCadenceValue(value: string): value is WasteVendorDeliveryCadence {
  switch (value) {
    case WasteVendorDeliveryCadence.Weekly:
    case WasteVendorDeliveryCadence.Monthly:
    case WasteVendorDeliveryCadence.Quarterly:
    case WasteVendorDeliveryCadence.Annual:
      return true;
    default:
      return false;
  }
}

function isIntakeValue(value: string): value is WasteVendorIntake {
  switch (value) {
    case WasteVendorIntake.VendorEmail:
    case WasteVendorIntake.SalesloftOutreach:
    case WasteVendorIntake.BespokeOutreach:
    case WasteVendorIntake.Portal:
    case WasteVendorIntake.Implementation:
    case WasteVendorIntake.Blocked:
      return true;
    default:
      return false;
  }
}

function initialTenantOptionFor(
  row: VendorDataRow | null,
  prefill: ExpectationPrefill | undefined
): TenantOption | null {
  if (row) return { id: row.tenantId, name: row.tenantName };
  if (prefill) return { id: prefill.tenantId, name: prefill.tenantName ?? prefill.tenantId };
  return null;
}

function initialVendorOptionFor(
  row: VendorDataRow | null,
  prefill: ExpectationPrefill | undefined
): VendorOption | null {
  if (row) {
    if (!row.vendorId) return null;
    return { id: row.vendorId, name: row.vendorName ?? row.vendorId };
  }
  if (prefill) {
    return {
      id: prefill.wasteManifestVendorId,
      name: prefill.vendorName ?? prefill.wasteManifestVendorId,
    };
  }
  return null;
}

/**
 * Add/edit dialog for a (tenant, vendor) delivery expectation. Handles both
 * flows in one component: `row` set means editing an existing expectation
 * (its id is submitted so the full-replace mutation updates in place);
 * `row` null with `prefill` set means creating a new expectation for an
 * already-identified pair (the identity fields are locked); both null means
 * a from-scratch add where the organization and vendor are picked here.
 *
 * `upsertTenantWasteVendor` replaces every field on every call, so the
 * submitted input always carries the complete field set — never a partial
 * patch — even for values the user didn't touch this time.
 */
export function ExpectationDialog({ open, onClose, row, prefill }: ExpectationDialogProps) {
  const alerts = useAlerts();
  const isEditingExpectation = row !== null && row.isExpected;
  const identityFixed = row !== null || prefill !== undefined;

  const [selectedTenant, setSelectedTenant] = useState<TenantOption | null>(
    initialTenantOptionFor(row, prefill)
  );
  const [selectedVendor, setSelectedVendor] = useState<VendorOption | null>(
    initialVendorOptionFor(row, prefill)
  );
  const [tenantSearch, setTenantSearch] = useState("");

  /**
   * The report only lists active, non-demo waste-module tenants, so the picker
   * must not offer anything else — an expectation saved against another
   * organization would immediately disappear from the only screen that manages
   * it. `upsertTenantWasteVendor` validates the same rule server-side, since
   * `hasFlag` also matches tenants relying on a default-true annotation while
   * the report requires an explicit config row.
   */
  const scopedTenantSearch =
    `hasFlag:${FeatureFlagKey.WasteModule} status:active ${tenantSearch}`.trim();
  const [vendorSearch, setVendorSearch] = useState("");

  const form = useForm<ExpectationFormType>({
    mode: "onChange",
    defaultValues: {
      tenantId: selectedTenant?.id ?? "",
      wasteManifestVendorId: selectedVendor?.id ?? "",
      cadence: row?.cadence ?? null,
      dueByDayOfMonth: row?.dueByDayOfMonth ?? null,
      intake: row?.intake ?? null,
      isActive: row?.isActive ?? false,
      notes: row?.notes ?? "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = form;

  // A weekly deadline is the end of the following week, which no day of the
  // month can express — the mutation rejects the pair. Disable the field and
  // clear anything already in it so switching an existing monthly row to weekly
  // can't submit a value the completeness math would ignore.
  const cadenceValue = useWatch({ control, name: "cadence" });
  const isWeeklyCadence = cadenceValue === WasteVendorDeliveryCadence.Weekly;
  useEffect(() => {
    if (isWeeklyCadence) {
      form.setValue("dueByDayOfMonth", null, { shouldValidate: true });
    }
  }, [isWeeklyCadence, form]);

  const [upsert, { loading: saving }] = useMutation(UPSERT_TENANT_WASTE_VENDOR, {
    // The query document (not a name string) refetches every active watcher
    // of it with whatever variables it's currently showing — the grid's own
    // live search/sort/page — matching the sibling Waste Vendors tab.
    refetchQueries: [VENDOR_DATA_COMPLETENESS_QUERY],
  });

  const onSubmit = async (data: ExpectationFormType) => {
    const rawCadence = data.cadence === "" ? null : data.cadence;
    const cadence = rawCadence !== null && isCadenceValue(rawCadence) ? rawCadence : null;

    const rawIntake = data.intake === "" ? null : data.intake;
    const intake = rawIntake !== null && isIntakeValue(rawIntake) ? rawIntake : null;

    // Every field is included on every submit, deliberately — the mutation
    // is a full replace, so a partial input here would silently blank
    // whatever this call omits.
    const input: UpsertTenantWasteVendorInput = {
      tenantId: data.tenantId,
      wasteManifestVendorId: data.wasteManifestVendorId,
      cadence,
      dueByDayOfMonth: data.dueByDayOfMonth,
      intake,
      isActive: data.isActive,
      notes: data.notes,
    };

    const variables = isEditingExpectation && row ? { input: { ...input, id: row.id } } : { input };

    try {
      await upsert({ variables });
      alerts.success(isEditingExpectation ? "Expectation updated." : "Expectation added.");
      onClose();
    } catch (err) {
      alerts.error(
        `Failed to save the expectation: ${err instanceof Error ? err.message : String(err)}`
      );
    }
  };

  return (
    <FormProvider {...form}>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        title={isEditingExpectation ? "Edit expectation" : "Add expectation"}
        primaryActionButtonProps={{
          onClick: handleSubmit(onSubmit),
          disabled: !isValid || saving,
          loading: saving,
        }}
      >
        <Stack spacing={3} sx={{ mt: 1 }}>
          {identityFixed ? (
            <TextField label="Organization" value={selectedTenant?.name ?? ""} disabled fullWidth />
          ) : (
            <Controller
              name="tenantId"
              control={control}
              rules={{ required: "Organization is required" }}
              render={({ field, fieldState: { error } }) => (
                <EncampAutocomplete
                  query={VENDOR_DATA_TENANTS_QUERY}
                  getItems={(data) => data?.tenants.items ?? []}
                  getOptionLabel={(option) => option.name}
                  variables={{
                    search: scopedTenantSearch,
                    pageSize: 25,
                    sort: [{ field: "name", sort: "asc" as const }],
                  }}
                  value={selectedTenant}
                  onChange={(value) => {
                    setSelectedTenant(value);
                    field.onChange(value?.id ?? "");
                  }}
                  onInputChange={setTenantSearch}
                  label="Organization"
                  error={error}
                />
              )}
            />
          )}

          {identityFixed ? (
            <TextField label="Vendor" value={selectedVendor?.name ?? ""} disabled fullWidth />
          ) : (
            <Controller
              name="wasteManifestVendorId"
              control={control}
              rules={{ required: "Vendor is required" }}
              render={({ field, fieldState: { error } }) => (
                <EncampAutocomplete
                  query={WASTE_MANIFEST_VENDORS_TABLE_QUERY}
                  getItems={(data) => data?.wasteManifestVendorsTable.items ?? []}
                  getOptionLabel={(option) => option.name}
                  variables={{
                    search: vendorSearch,
                    pageSize: 25,
                    sort: [{ field: "name", sort: "asc" as const }],
                  }}
                  value={selectedVendor}
                  onChange={(value) => {
                    setSelectedVendor(value);
                    field.onChange(value?.id ?? "");
                  }}
                  onInputChange={setVendorSearch}
                  label="Vendor"
                  error={error}
                />
              )}
            />
          )}

          <FormSelect
            name="cadence"
            label="Cadence"
            control={control}
            selectItems={CADENCE_SELECT_ITEMS}
            clearable={false}
          />

          <FormTextField
            name="dueByDayOfMonth"
            label="Due by day of month"
            control={control}
            intOnly
            disabled={isWeeklyCadence}
            helperText={
              isWeeklyCadence ? "Weekly data is due by the end of the following week." : undefined
            }
            textFieldProps={{ type: "number" }}
            inputProps={{ min: 1, max: 31 }}
            rules={{
              // Loosely typed to match FormTextField's generic-less
              // RegisterOptions — intOnly guarantees the runtime value is
              // always a number or null, never a string to parse.
              validate: (value: string | number | boolean | null) => {
                if (typeof value !== "number") return true;
                if (value < 1 || value > 31) return "Due day must be between 1 and 31.";
                return true;
              },
            }}
          />

          <FormSelect
            name="intake"
            label="Intake"
            control={control}
            selectItems={INTAKE_SELECT_ITEMS}
            clearable={false}
          />

          <ToggleField name="isActive" label="Active" control={control} />

          <FormTextField
            name="notes"
            label="Notes"
            control={control}
            textFieldProps={{ multiline: true, minRows: 3 }}
          />
        </Stack>
      </Dialog>
    </FormProvider>
  );
}
